import type { Image } from "api/schemas";
import { constructImageUrl, type sizePrefix } from "utils/constructImageUrl";

const PLACEHOLDER_IMAGE = "/placeholder.jpg";

type ListingUnitImage = Image & { imageSignature?: string };

const toMedia = (
  image: ListingUnitImage | undefined,
  imageSignature?: string
) => {
  const signature = image?.imageSignature ?? imageSignature;
  if (!image?.CFURL || !image?.name || !signature) return null;
  return {
    id: 0,
    url: `${image.CFURL}${image.name}`,
    media_type: "image" as const,
    data: { CFURL: image.CFURL, name: image.name, signature },
  };
};

const buildListingImageUrl = (
  image: ListingUnitImage | undefined,
  imageSignature: string | undefined,
  width: sizePrefix
): string =>
  constructImageUrl(toMedia(image, imageSignature), { width }) ??
  PLACEHOLDER_IMAGE;

/** Ensures card image URLs use API `additional_images` (published ORM may only read camelCase). */
export function enrichListingUnit<T extends Record<string, unknown>>(
  unit: T,
  imageSignature?: string
): T & {
  imageUrl: string;
  secondaryImageUrl: string;
  tertiaryImageUrl: string;
  variant: 1 | 3;
} {
  const record = unit as Record<string, unknown>;
  const additional =
    (record.additional_images as ListingUnitImage[] | undefined) ??
    (record.additionalImages as ListingUnitImage[] | undefined) ??
    [];
  const primaryImage = record.image as ListingUnitImage | undefined;
  const variant: 1 | 3 = Number(record.variant) === 3 ? 3 : 1;

  return {
    ...unit,
    variant,
    imageUrl: buildListingImageUrl(primaryImage, imageSignature, 1080),
    secondaryImageUrl: buildListingImageUrl(additional[0], imageSignature, 480),
    tertiaryImageUrl: buildListingImageUrl(additional[1], imageSignature, 480),
  };
}
